import React, { useState, useContext } from 'react';
import {
    Modal,
    Box,
    Typography,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    Divider,
    Chip,
    Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { Context as AppContext } from "../../context/AppContext";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../config/firebase";
import { toast } from "react-toastify";
import { capitalizeFirstWord } from '../../utils/functions';

const MatchInternModal = ({ open, onClose, intern }) => {
    const {
        state: { mentors },
        updateInternData,
        createInternship,
        getAllInterns,
        getAllMentors,
        updateSomeUserData,
        sendMatchedEmails
    } = useContext(AppContext);
    const [selectedMentorUid, setSelectedMentorUid] = useState('');
    const [loading, setLoading] = useState(false);

    // Filter mentors who are available (no mentees or empty mentees array)
    const getAvailableMentors = () => {
        if (!mentors) return [];
        return mentors.filter(mentor => {
            console.log(mentor);
            return (!mentor.mentees || mentor.mentees.length === 0)
        });
    };

    const availableMentors = getAvailableMentors();

    const handleMatch = async () => {
        if (!selectedMentorUid || !intern) {
            toast.error('Please select a mentor');
            return;
        }

        setLoading(true);
        try {
            // Get the selected mentor's current data
            const mentorRef = doc(db, "users", selectedMentorUid);
            const mentorDoc = await getDoc(mentorRef);

            if (!mentorDoc.exists()) {
                toast.error('Mentor not found');
                setLoading(false);
                return;
            }

            const mentorData = mentorDoc.data();

            // Create internship UID
            const internshipUid = crypto.randomUUID();

            // Update mentor's mentees array
            const updatedMentorData = {
                ...mentorData,
                mentees: mentorData.mentees ? [...mentorData.mentees, intern.uid] : [intern.uid]
            };

            // Update mentor data
            await updateSomeUserData(updatedMentorData);

            // Update intern data
            await updateInternData({
                mentorUid: selectedMentorUid,
                uid: intern.uid,
                acceptedAt: Date.now(),
                internshipUid: internshipUid,
            });

            // Create internship
            await createInternship({
                mentorUid: selectedMentorUid,
                uid: internshipUid,
                studentUid: intern.uid,
                startDate: new Date().toISOString(),
                endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
                title: `Internship for ${capitalizeFirstWord(intern.name)}`,
                goals: [],
                tasks: [],
                journalEntries: []
            });

            toast.success(`Successfully matched ${capitalizeFirstWord(intern.name)} with ${capitalizeFirstWord(mentorData.name)}`);

            // Send matched emails to both mentor and mentee
            try {
                await sendMatchedEmails(
                    mentorData.email || '',
                    mentorData.name || '',
                    intern.email || '',
                    intern.name || '',
                    intern.school || 'AIDEN Program'
                );
                console.log('Matched emails sent successfully');
            } catch (emailError) {
                console.error('Error sending matched emails:', emailError);
                toast.warning('Match created successfully, but failed to send notification emails');
            }

            // Refresh data
            getAllInterns();
            getAllMentors();

            // Close modal and reset
            handleClose();

        } catch (error) {
            console.error('Error matching intern with mentor:', error);
            toast.error('Failed to create match. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setSelectedMentorUid('');
        onClose();
    };

    const selectedMentor = mentors?.find(mentor => mentor.uid === selectedMentorUid);

    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="match-intern-modal"
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Paper
                sx={{
                    width: { xs: '90%', sm: '500px' },
                    maxHeight: '90vh',
                    overflow: 'auto',
                    p: 4,
                    borderRadius: 2,
                }}
            >
                <Stack spacing={3}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h5" fontWeight="bold">
                            Match Intern with Mentor
                        </Typography>
                        <Button
                            onClick={handleClose}
                            color="inherit"
                            size="small"
                            sx={{ minWidth: 'auto', p: 1 }}
                        >
                            <CloseIcon />
                        </Button>
                    </Stack>

                    <Divider />

                    {intern && (
                        <Box>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                Intern Details
                            </Typography>
                            <Stack spacing={1}>
                                <Typography variant="body1">
                                    <strong>Name:</strong> {capitalizeFirstWord(intern.name)}
                                </Typography>
                                {intern.school && (
                                    <Typography variant="body2">
                                        <strong>School:</strong> {intern.school}
                                    </Typography>
                                )}
                                {intern.location && (
                                    <Typography variant="body2">
                                        <strong>Location:</strong> {intern.location}
                                    </Typography>
                                )}
                                {intern.interests && (
                                    <Box>
                                        <Typography variant="body2" component="span">
                                            <strong>Interests:</strong>{' '}
                                        </Typography>
                                        <Typography variant="body2" component="span">
                                            {intern.interests}
                                        </Typography>
                                    </Box>
                                )}
                                {intern.mentorApplications && (
                                    <Box>
                                        <Typography variant="body2" component="span">
                                            <strong>Applications:</strong>{' '}
                                        </Typography>
                                        <Chip
                                            label={`${intern.mentorApplications.length} pending`}
                                            color="warning"
                                            size="small"
                                        />
                                    </Box>
                                )}
                            </Stack>
                        </Box>
                    )}

                    <Divider />

                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Select Available Mentor
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Only mentors with no current mentees are shown
                        </Typography>

                        <FormControl fullWidth sx={{ mt: 2 }}>
                            <InputLabel id="mentor-select-label">Choose Mentor</InputLabel>
                            <Select
                                labelId="mentor-select-label"
                                value={selectedMentorUid}
                                label="Choose Mentor"
                                onChange={(e) => setSelectedMentorUid(e.target.value)}
                                disabled={loading}
                            >
                                {availableMentors.length === 0 ? (
                                    <MenuItem disabled>
                                        No available mentors found
                                    </MenuItem>
                                ) : (
                                    availableMentors.map((mentor) => (
                                        <MenuItem key={mentor.uid} value={mentor.uid} disabled={!mentor.isMentor}>
                                            <Stack>
                                                <Typography variant="body1" fontWeight="medium">
                                                    {capitalizeFirstWord(mentor.name)} - {mentor.company}
                                                </Typography>
                                                {mentor.jobTitle && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {mentor.jobTitle} - {mentor.uid}
                                                    </Typography>
                                                )}
                                                
                                    
                                                {mentor.jobDescription && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        {mentor.jobDescription}
                                                    </Typography>
                                                )}

                                                {mentor.isMentor ? (
                                                    <Typography variant="body2" color="green">
                                                        Mentorship accepted
                                                    </Typography>
                                                ) : (
                                                    <Typography variant="body2" color="red">
                                                        Not a mentor in this cycle!
                                                    </Typography>
                                                )}
                                                <Divider sx={{ mt: 1, mb: 1 }} />
                                            </Stack>
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                        </FormControl>

                        {selectedMentor && (
                            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                                <Typography variant="body2" fontWeight="bold">
                                    Selected Mentor Details:
                                </Typography>
                                <Typography variant="body2">
                                    {capitalizeFirstWord(selectedMentor.name)}
                                </Typography>
                                {selectedMentor.company && (
                                    <Typography variant="body2" color="text.secondary">
                                        {selectedMentor.company}
                                    </Typography>
                                )}
                                {selectedMentor.jobTitle && (
                                    <Typography variant="body2" color="text.secondary">
                                        {selectedMentor.jobTitle}
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Box>

                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button
                            variant="outlined"
                            onClick={handleClose}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleMatch}
                            disabled={!selectedMentorUid || loading || availableMentors.length === 0}
                            startIcon={<PersonAddIcon />}
                            sx={{
                                backgroundColor: '#1976d2',
                                '&:hover': { backgroundColor: '#115293' }
                            }}
                        >
                            {loading ? 'Creating Match...' : 'Create Match'}
                        </Button>
                    </Stack>
                </Stack>
            </Paper>
        </Modal>
    );
};

export default MatchInternModal; 